import { BN } from "@project-serum/anchor";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { divide, multiply } from "mathjs";
import { TradedRightType } from "../../../common/enums/common.enums";

import {
  IClubTreasury,
  IMembersRights,
  IReservedRights,
  ITreasuryTokenAccInfo,
  IWithdrawalMembersInfo,
} from "../../../common/interfaces/clubs.interface";
import {
  getDecimalsPerMint,
  getMintDecimalsDivider,
} from "../../../utilities/helpers";
import { claimWithdrawalAmount } from "../../methods/proposals";
import {
  escrowProgramFactory,
  governanceSeed,
  programFactory,
  splGovernanceProgram,
  treasuryDataSeed,
  unqClubSeed,
  withdrawalDataSeed,
  withdrawalRecordSeed,
  withdrawalSeed,
} from "../utilities";
import {
  getDepositAmountForMemberBasedOnSelectedFundraises,
  getInfoForMintAccounts,
  getMemberReservedRights,
} from "./common.helpers";

export async function getWithdrawalAccountsForMember(
  wallet: AnchorWallet,
  realmName: string,
  treasury: IClubTreasury,
  memberRights?: IMembersRights
) {
  try {
    if (memberRights && memberRights.ownership === 0) {
      return [];
    }
    const escrowProgram = escrowProgramFactory();
    let userClaimed: boolean;
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const withdrawalMembersInfo: IWithdrawalMembersInfo[] = [];
    const allWithdrawals = (
      await escrowProgram.account.withdrawalData.all()
    ).filter(
      (withdrawalData) =>
        withdrawalData.account.realm.toString() === realmAddress.toString()
    );

    for (const withdrawalData of allWithdrawals) {
      const { depositedAmount } =
        await getDepositAmountForMemberBasedOnSelectedFundraises(
          withdrawalData.account.fundraiseNumber,
          realmName,
          wallet.publicKey,
          getDecimalsPerMint(treasury.denominatedTokenInfo),
          withdrawalData.account.proposalCreatedAt.toNumber()
        );

      const [withdrawal] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          withdrawalData.account.proposal.toBuffer(),
          withdrawalData.account.treasury.toBuffer(),
          withdrawalSeed,
        ],
        escrowProgram.programId
      );
      const [withdrawalRecord] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          withdrawal.toBuffer(),
          withdrawalRecordSeed,
          wallet.publicKey.toBuffer(),
        ],
        escrowProgram.programId
      );

      try {
        const withdrawalRecordAccount =
          await escrowProgram.account.withdrawalRecord.fetch(withdrawalRecord);
        userClaimed = withdrawalRecordAccount.hasWithdrawn;
      } catch (error) {
        userClaimed = false;
      }

      const withdrawalMintInfo = await getInfoForMintAccounts([
        withdrawalData.account.withdrawalMint,
      ]);
      const decimals = getDecimalsPerMint(treasury.denominatedTokenInfo);

      let withdrawAmount = multiply(
        depositedAmount /
          (withdrawalData.account.totalFinancialPower.toNumber() / decimals),
        withdrawalData.account.withdrawalAmount
          .div(new BN(getMintDecimalsDivider(withdrawalMintInfo[0].decimals)))
          .toNumber()
      );

      const memberReservedRights = getMemberReservedRights(
        wallet.publicKey,
        treasury.reservedRights
      );

      if (memberReservedRights !== undefined) {
        withdrawAmount =
          withdrawAmount +
          multiply(
            memberReservedRights.amountOfRights /
              (withdrawalData.account.totalFinancialPower.toNumber() /
                decimals),
            withdrawalData.account.withdrawalAmount.toNumber() /
              getMintDecimalsDivider(withdrawalMintInfo[0].decimals)
          );
      }

      if (withdrawAmount === 0) continue;
      withdrawalMembersInfo.push({
        hasUserClaimed: userClaimed,
        proposal: withdrawalData.account.proposal,
        withdrawAmount: withdrawAmount,
        withdrawal: withdrawal,
        withdrawalData: withdrawalData.publicKey,
        withdrawalMint: withdrawalData.account.withdrawalMint,
        withdrawalMintDecimals: withdrawalMintInfo[0].decimals,
        withdrawalMintSymbol: withdrawalMintInfo[0].symbol,
      });
    }
    return withdrawalMembersInfo;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const claimWithdrawalTokens = async (
  wallet: AnchorWallet,
  withdrawal: PublicKey,
  withdrawalData: PublicKey,
  clubName: string,
  withdrawalMembersInfo: IWithdrawalMembersInfo[]
) => {
  try {
    await claimWithdrawalAmount(wallet!, withdrawal, withdrawalData, clubName);
    const withdrawalToUpdate = withdrawalMembersInfo.findIndex(
      (item) => item.withdrawal.toString() === withdrawal.toString()
    );
    const updatedWithdrawals = [...withdrawalMembersInfo];
    updatedWithdrawals[withdrawalToUpdate].hasUserClaimed = true;
    return updatedWithdrawals;
  } catch (error: any) {
    throw error;
  }
};

export const getUserWithdrawalRecord = async (
  withdrawalDataAddress: PublicKey,
  proposalAddress: PublicKey,
  treasuryAddress: PublicKey,
  wallet: AnchorWallet
): Promise<IWithdrawalMembersInfo> => {
  try {
    const escrowProgram = escrowProgramFactory();
    const [withdrawalRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        withdrawalDataAddress.toBuffer(),
        withdrawalRecordSeed,
        wallet.publicKey.toBuffer(),
      ],
      escrowProgram.programId
    );

    const [withdrawal] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        proposalAddress.toBuffer(),
        treasuryAddress.toBuffer(),
        withdrawalSeed,
      ],
      escrowProgram.programId
    );
    const withdrawalRecordData =
      await escrowProgram.account.withdrawalRecord.fetch(withdrawalRecord);

    const withdrawalMintInfo = await getInfoForMintAccounts([
      withdrawalRecordData.withdrawalMint,
    ]);

    return {
      hasUserClaimed: withdrawalRecordData.hasWithdrawn,
      withdrawal: withdrawal,
      withdrawAmount: withdrawalRecordData.withdrawnAmount.toNumber(),
      withdrawalData: withdrawalDataAddress,
      proposal: proposalAddress,
      withdrawalMintDecimals: withdrawalMintInfo[0].decimals,
      withdrawalMint: withdrawalRecordData.withdrawalMint,
      withdrawalMintSymbol: withdrawalMintInfo[0].symbol,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export async function getSingleWithdrawalAccount(
  clubName: string,
  proposal: PublicKey,
  treasury: PublicKey,
  wallet: AnchorWallet,
  offerAmount: number,
  memberRights: IMembersRights | undefined,
  mint: PublicKey,
  decimals: number,
  symbol: string,
  denominatedCurrency: ITreasuryTokenAccInfo | undefined,
  fundraiseIndex: number,
  timestampCreated: number,
  reservedRights: IReservedRights | undefined
) {
  try {
    const denominatedDecimalsPerMintAmount =
      getDecimalsPerMint(denominatedCurrency);
    const withdrawalDecimaslPerMintAmount = getMintDecimalsDivider(decimals);
    const memberReservedRights = getMemberReservedRights(
      wallet.publicKey,
      reservedRights
    );
    const escrowProgram = escrowProgramFactory();
    const clubProgram = programFactory();
    const [withdrawal] = await PublicKey.findProgramAddress(
      [unqClubSeed, proposal.toBuffer(), treasury.toBuffer(), withdrawalSeed],
      escrowProgram.programId
    );

    const [withdrawalData] = await PublicKey.findProgramAddress(
      [unqClubSeed, withdrawal.toBuffer(), withdrawalDataSeed],
      escrowProgram.programId
    );

    const [withdrawalRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        withdrawal.toBuffer(),
        withdrawalRecordSeed,
        wallet.publicKey.toBuffer(),
      ],
      escrowProgram.programId
    );

    let hasUserClaimed: boolean = false;
    let withdrawAmount: number = 0;
    let withdrawalDataAccount;
    let totalFinancialPower: number;

    try {
      withdrawalDataAccount = await escrowProgram.account.withdrawalData.fetch(
        withdrawalData
      );
      totalFinancialPower =
        withdrawalDataAccount.totalFinancialPower.toNumber() /
        denominatedDecimalsPerMintAmount;
    } catch (error) {
      const [treasuryData] = await PublicKey.findProgramAddress(
        [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
        clubProgram.programId
      );
      const treasuryDataAccount = await clubProgram.account.treasuryData.fetch(
        treasuryData
      );
      totalFinancialPower = divide(
        treasuryDataAccount.totalFinancialPower.toNumber(),
        denominatedDecimalsPerMintAmount
      );
      let totalDeposited = 0;
      for (let i = fundraiseIndex; i > 0; i--) {
        const depositRecordForFundraise = memberRights?.depositRecords.find(
          (dep) => dep.fundraiseIndex === i
        );
        if (depositRecordForFundraise) {
          totalDeposited =
            depositRecordForFundraise.accumulatedAmount +
            (depositRecordForFundraise.tradedRight
              ? depositRecordForFundraise.tradedRight?.reduce((acc, val) => {
                  if (val.timestamp > timestampCreated) {
                    return acc + 0;
                  }
                  if (
                    val.tradedType === TradedRightType.Buy ||
                    val.tradedType === TradedRightType.BuyReserved
                  ) {
                    return acc + val.amount;
                  } else {
                    return acc - val.amount;
                  }
                }, 0)
              : 0);

          break;
        }
      }

      withdrawAmount = multiply(
        divide(
          totalDeposited,
          treasuryDataAccount.totalFinancialPower.toNumber()
        ),
        offerAmount
      );

      if (memberReservedRights !== undefined) {
        withdrawAmount =
          withdrawAmount +
          multiply(
            divide(
              memberReservedRights.amountOfRights *
                denominatedDecimalsPerMintAmount,
              treasuryDataAccount.totalFinancialPower.toNumber()
            ),
            offerAmount
          );
      }

      hasUserClaimed = false;
      const memberWithdrawalData: IWithdrawalMembersInfo = {
        hasUserClaimed,
        proposal,
        withdrawAmount: divide(withdrawAmount, withdrawalDecimaslPerMintAmount),
        withdrawal,
        withdrawalData,
        withdrawalMintDecimals: decimals,
        withdrawalMint: mint,
        withdrawalMintSymbol: symbol,
      };
      return { memberWithdrawalData, totalFinancialPower };
    }

    try {
      const withdrawalRecordAccount =
        await escrowProgram.account.withdrawalRecord.fetch(withdrawalRecord);
      hasUserClaimed = withdrawalRecordAccount.hasWithdrawn;
      withdrawAmount = withdrawalRecordAccount.withdrawnAmount
        .div(new BN(withdrawalDecimaslPerMintAmount))
        .toNumber();
    } catch (error) {
      hasUserClaimed = false;

      const { depositedAmount } =
        await getDepositAmountForMemberBasedOnSelectedFundraises(
          withdrawalDataAccount.fundraiseNumber,
          clubName,
          wallet.publicKey,
          denominatedDecimalsPerMintAmount,
          timestampCreated
        );

      withdrawAmount = multiply(
        divide(
          depositedAmount * denominatedDecimalsPerMintAmount,
          withdrawalDataAccount.totalFinancialPower.toNumber()
        ),
        withdrawalDataAccount.withdrawalAmount
          .div(new BN(withdrawalDecimaslPerMintAmount))
          .toNumber()
      );

      if (memberReservedRights !== undefined) {
        withdrawAmount =
          withdrawAmount +
          multiply(
            divide(
              memberReservedRights.amountOfRights *
                denominatedDecimalsPerMintAmount,
              withdrawalDataAccount.totalFinancialPower.toNumber()
            ),
            withdrawalDataAccount.withdrawalAmount.toNumber() /
              withdrawalDecimaslPerMintAmount
          );
      }
    }

    const memberWithdrawalData: IWithdrawalMembersInfo = {
      hasUserClaimed: hasUserClaimed,
      proposal: withdrawalDataAccount.proposal,
      withdrawAmount: withdrawAmount,
      withdrawal,
      withdrawalData,
      withdrawalMintDecimals: decimals,
      withdrawalMint: mint,
      withdrawalMintSymbol: symbol,
    };
    return { memberWithdrawalData, totalFinancialPower };
  } catch (error) {
    console.log(error);
    throw error;
  }
}
